import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Modal, Input, useToast } from '../../components/primitives';
import { ListPage, ListTable, type ListColumn } from '../../components/list';
import { useActiveStavba } from '../stavba-context/StavbaContext';
import {
  useProjectDocs,
  useUploadProjectDoc,
  useDeleteProjectDoc,
  useProjectDocShares,
  useShareProjectDoc,
  useUnshareProjectDoc,
  useProjectDocPublicLink,
  useCreatePublicLink,
  useDeletePublicLink,
} from '../../api/hooks';
import { api } from '../../lib/api';
import type { ProjectFile } from '../../api/schemas';

const catTones: Record<string, 'yellow' | 'success' | 'neutral' | 'danger'> = {
  projektova_dokumentace: 'yellow',
  rozpocty: 'neutral',
  stavebni_denik: 'danger',
  fotodokumentace: 'neutral',
  smlouvy: 'success',
  objednavky: 'neutral',
};

function formatSize(bytes: number): string {
  if (!Number.isFinite(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} kB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('cs-CZ');
}

/** „David Harant" → „DH"; jednoslovné → první písmeno. */
function initials(name?: string | null): string {
  if (!name) return '—';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

/** Stáhne soubor přes autentizovaný endpoint (vrací blob) a uloží lokálně. */
async function downloadDoc(stavbaId: number, file: ProjectFile) {
  const blob = await api.get<Blob>(`/stavby/${stavbaId}/project-docs/${file.id}`);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.filename || file.nazev || 'soubor';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Otevře soubor v nové záložce/okně (náhled). Endpoint vyžaduje auth hlavičku,
 * proto nelze použít přímý odkaz — načteme blob a otevřeme object URL.
 * Okno otevřeme synchronně (v rámci kliknutí) kvůli popup blockeru.
 */
async function openDoc(stavbaId: number, file: ProjectFile) {
  const w = window.open('', '_blank');
  try {
    const blob = await api.get<Blob>(`/stavby/${stavbaId}/project-docs/${file.id}`);
    const url = URL.createObjectURL(blob);
    if (w) w.location.href = url;
    else window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    if (w) w.close();
    throw e;
  }
}

export function ProjectDocsPage() {
  const navigate = useNavigate();
  const { activeStavbaId, activeStavba } = useActiveStavba();
  const { data: files = [], isLoading, error } = useProjectDocs(activeStavbaId);
  const upload = useUploadProjectDoc(activeStavbaId);
  const del = useDeleteProjectDoc(activeStavbaId);
  const toast = useToast();
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragDepth = useRef(0);

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [shareFiles, setShareFiles] = useState<ProjectFile[] | null>(null);

  function toggleRow(f: ProjectFile) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(f.id)) next.delete(f.id);
      else next.add(f.id);
      return next;
    });
  }

  function toggleAll() {
    setSelected((prev) => (prev.size === files.length ? new Set() : new Set(files.map((f) => f.id))));
  }

  const selectedFiles = files.filter((f) => selected.has(f.id));

  async function handleDeleteOne(f: ProjectFile) {
    if (!window.confirm(`Smazat „${f.nazev || f.filename}"? Akci nelze vrátit.`)) return;
    await del.mutateAsync(f.id);
    setSelected((prev) => {
      const next = new Set(prev);
      next.delete(f.id);
      return next;
    });
    toast.success('Dokument smazán');
  }

  async function handleDeleteSelected() {
    if (selectedFiles.length === 0) return;
    if (!window.confirm(`Smazat ${selectedFiles.length} dokumentů? Akci nelze vrátit.`)) return;
    for (const f of selectedFiles) await del.mutateAsync(f.id);
    setSelected(new Set());
    toast.success(`Smazáno ${selectedFiles.length} dokumentů`);
  }

  const columns: ListColumn<ProjectFile>[] = [
    {
      key: 'nazev',
      label: 'Soubor',
      primary: true,
      width: 'minmax(160px, 4fr)',
      render: (f) => (
        <button
          type="button"
          onClick={() => openDoc(activeStavbaId!, f).catch(() => toast.error('Otevření se nezdařilo'))}
          title="Otevřít v okně"
          style={{
            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            color: 'inherit', font: 'inherit', textAlign: 'left', textDecoration: 'underline',
            textDecorationColor: 'var(--sd-border, var(--sd-w-d8d4c5, #d8d4c5))', textUnderlineOffset: 3,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%',
          }}
        >
          {f.nazev || f.filename || '—'}
        </button>
      ),
    },
    {
      key: 'kategorie',
      label: 'Kategorie',
      width: '92px',
      render: (f) => (
        <span style={{ fontSize: 10.5 }}>
          <Badge tone={catTones[f.kategorie?.toLowerCase()] ?? 'neutral'}>
            {f.kategorie || 'Ostatní'}
          </Badge>
        </span>
      ),
    },
    {
      key: 'owner_name',
      label: 'Vložil',
      width: '56px',
      dim: true,
      render: (f) => <span title={f.owner_name || ''}>{initials(f.owner_name)}</span>,
    },
    { key: 'created_at', label: 'Nahráno', width: '84px', mono: true, dim: true, render: (f) => <span style={{ fontSize: 11 }}>{formatDate(f.created_at)}</span> },
    { key: 'size', label: 'Velikost', width: '78px', mono: true, dim: true, align: 'right', render: (f) => <span style={{ fontSize: 11 }}>{formatSize(f.size)}</span> },
  ];

  async function uploadFiles(list: FileList | File[]) {
    if (!activeStavbaId) return;
    const arr = Array.from(list);
    for (const file of arr) {
      await upload.mutateAsync({ file });
    }
    if (arr.length > 0) toast.success(arr.length === 1 ? 'Soubor nahrán' : `Nahráno ${arr.length} souborů`);
  }

  function handleUploadClick() {
    fileInput.current?.click();
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const list = e.target.files;
    if (!list || list.length === 0) return;
    await uploadFiles(list);
    if (fileInput.current) fileInput.current.value = '';
  }

  function handleDragEnter(e: React.DragEvent) {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    dragDepth.current += 1;
    setIsDragging(true);
  }

  function handleDragOver(e: React.DragEvent) {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }

  function handleDragLeave(e: React.DragEvent) {
    if (!e.dataTransfer.types.includes('Files')) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragging(false);
  }

  async function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    dragDepth.current = 0;
    setIsDragging(false);
    const dropped = e.dataTransfer.files;
    if (dropped && dropped.length > 0) await uploadFiles(dropped);
  }

  if (!activeStavbaId) {
    return (
      <ListPage breadcrumb={['Realizace', 'Projektová dokumentace']} title="Projektová dokumentace" subtitle="Nejprve vyberte stavbu">
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--sd-doc-dim)' }}>
          Vyberte aktivní stavbu.
        </div>
      </ListPage>
    );
  }

  return (
    <ListPage
      breadcrumb={['Realizace', activeStavba?.nazev ?? '', 'Projektová dokumentace'].filter(Boolean)}
      title="Projektová dokumentace"
      subtitle={
        isLoading
          ? 'Načítám…'
          : error
            ? 'Chyba při načítání'
            : `${files.length} souborů`
      }
      status={isLoading ? 'neutral' : error ? 'draft' : 'done'}
      statusText={isLoading ? 'Načítám…' : error ? 'Chyba' : `${files.length} souborů`}
      actions={
        <>
          <input
            ref={fileInput}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <Button variant="secondary" onClick={() => navigate('/app/dokumenty-projektu/nasdilene')}>
            📥 Nasdílené mně
          </Button>
          <Button variant="primary" onClick={handleUploadClick} disabled={upload.isPending}>
            {upload.isPending ? 'Nahrávám…' : '+ Nahrát soubor'}
          </Button>
        </>
      }
      searchPlaceholder="Hledat podle názvu, kategorie…"
    >
      {error ? (
        <div style={{ padding: 24, borderRadius: 4, background: 'var(--sd-danger-soft)', color: 'var(--sd-danger)' }}>
          Chyba: {error instanceof Error ? error.message : 'neznámá'}
        </div>
      ) : (
        <div
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={{ position: 'relative' }}
        >
          {/* Vždy viditelná drop-zóna — připomíná možnost přetažení */}
          <button
            type="button"
            onClick={handleUploadClick}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              marginBottom: 14,
              padding: '14px 16px',
              borderRadius: 8,
              border: `2px dashed ${isDragging ? 'var(--sd-primary, #f0b323)' : 'var(--sd-border, var(--sd-w-d8d4c5, #d8d4c5))'}`,
              background: isDragging ? 'rgba(240, 179, 35, 0.10)' : 'var(--sd-surface-soft, var(--sd-w-faf8f3, #faf8f3))',
              color: 'var(--sd-text-dim, #6b7280)',
              fontSize: 13.5,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'border-color .12s, background .12s',
            }}
          >
            <span aria-hidden style={{ fontSize: 16 }}>⬇</span>
            Přetáhněte soubory sem nebo klikněte pro výběr (lze i více souborů)
          </button>

          {/* Hromadný panel akcí */}
          {selectedFiles.length > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginBottom: 12,
                padding: '8px 12px',
                borderRadius: 6,
                background: 'var(--sd-primary-soft, #fdf4dc)',
                border: '1px solid var(--sd-primary, #f0b323)',
              }}
            >
              <strong style={{ fontSize: 13 }}>{selectedFiles.length} vybráno</strong>
              <div style={{ flex: 1 }} />
              <Button size="sm" variant="secondary" onClick={() => setShareFiles(selectedFiles)}>
                🔗 Sdílet vybrané
              </Button>
              <Button size="sm" variant="danger" onClick={handleDeleteSelected} disabled={del.isPending}>
                🗑 Smazat vybrané
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
                Zrušit výběr
              </Button>
            </div>
          )}

          <ListTable
            columns={columns}
            rows={files}
            rowKey={(f) => f.id}
            fluid
            selectable
            selectedKeys={selected}
            onToggleRow={toggleRow}
            onToggleAll={toggleAll}
            actionsWidth="150px"
            rowActions={(f) => (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => downloadDoc(activeStavbaId!, f).catch(() => toast.error('Stažení se nezdařilo'))}
                  title="Stáhnout"
                >
                  ⬇
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShareFiles([f])} title="Sdílet">
                  🔗
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleDeleteOne(f)} title="Smazat" disabled={del.isPending}>
                  🗑
                </Button>
              </>
            )}
            emptyTitle="Žádné dokumenty"
            emptyText="Nahrajte PD, statiku, smlouvy nebo úřední dokumenty — nebo soubory přetáhněte sem."
          />
        </div>
      )}

      {shareFiles && shareFiles.length === 1 && (
        <SingleShareModal
          file={shareFiles[0]}
          stavbaId={activeStavbaId}
          onClose={() => setShareFiles(null)}
        />
      )}
      {shareFiles && shareFiles.length > 1 && (
        <BulkShareModal
          files={shareFiles}
          stavbaId={activeStavbaId}
          onClose={() => setShareFiles(null)}
        />
      )}
    </ListPage>
  );
}

function publicUrl(token: string): string {
  return `${window.location.origin}/api/public/project-docs/${token}`;
}

/** Sdílení jednoho dokumentu — s uživatelem (e-mail) + veřejný odkaz. */
function SingleShareModal({
  file,
  stavbaId,
  onClose,
}: {
  file: ProjectFile;
  stavbaId: number;
  onClose: () => void;
}) {
  const toast = useToast();
  const { data: shares = [] } = useProjectDocShares(stavbaId, file.id);
  const { data: link } = useProjectDocPublicLink(stavbaId, file.id);
  const share = useShareProjectDoc(stavbaId);
  const unshare = useUnshareProjectDoc(stavbaId);
  const createLink = useCreatePublicLink(stavbaId);
  const deleteLink = useDeletePublicLink(stavbaId);
  const [email, setEmail] = useState('');
  const [err, setErr] = useState<string | null>(null);

  async function handleShare() {
    const e = email.trim();
    if (!e) return;
    setErr(null);
    try {
      await share.mutateAsync({ fileId: file.id, email: e });
      setEmail('');
    } catch (ex: unknown) {
      const status = (ex as { status?: number })?.status;
      setErr(
        status === 404 ? 'Uživatel s tímto e-mailem nenalezen.'
          : status === 409 ? 'Tomuto uživateli už je soubor nasdílen.'
          : status === 400 ? 'Nelze sdílet sám se sebou.'
          : 'Sdílení se nezdařilo.',
      );
    }
  }

  async function copyLink() {
    if (!link) return;
    await navigator.clipboard.writeText(publicUrl(link.token));
    toast.success('Odkaz zkopírován');
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Sdílet: ${file.nazev || file.filename}`}
      size="md"
      footer={<Button variant="secondary" onClick={onClose}>Zavřít</Button>}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {/* Sdílení s uživatelem */}
        <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--sd-text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Sdílet s uživatelem aplikace
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="e-mail uživatele"
                onKeyDown={(e) => { if (e.key === 'Enter') handleShare(); }}
              />
            </div>
            <Button variant="primary" onClick={handleShare} disabled={share.isPending || !email.trim()}>
              {share.isPending ? 'Sdílím…' : 'Sdílet'}
            </Button>
          </div>
          {err && <div style={{ fontSize: 12.5, color: 'var(--sd-danger)' }}>{err}</div>}
          {shares.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {shares.map((s) => (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '6px 10px', background: 'var(--sd-w-f7f4ee, #f7f4ee)', border: '1px solid var(--sd-w-e7e1d4, #e7e1d4)', borderRadius: 4 }}>
                  <span style={{ fontSize: 13 }}>
                    <strong>{s.shared_with_name || s.shared_with_email}</strong>
                    {s.shared_with_name && <span style={{ color: 'var(--sd-text-dim)' }}> · {s.shared_with_email}</span>}
                  </span>
                  <Button size="sm" variant="ghost" onClick={() => unshare.mutate({ fileId: file.id, shareId: s.id })} disabled={unshare.isPending}>
                    Zrušit
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Veřejný odkaz */}
        <section style={{ display: 'flex', flexDirection: 'column', gap: 10, borderTop: '1px solid var(--sd-w-e7e1d4, #e7e1d4)', paddingTop: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--sd-text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Veřejný odkaz ke stažení
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--sd-text-dim)' }}>
            Kdokoli s odkazem si soubor stáhne <strong>bez přihlášení</strong>.
          </div>
          {link ? (
            <>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  readOnly
                  value={publicUrl(link.token)}
                  onFocus={(e) => e.target.select()}
                  style={{ flex: 1, minWidth: 220, padding: '7px 10px', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, fontSize: 12.5, fontFamily: 'var(--sd-font-mono, monospace)' }}
                />
                <Button size="sm" variant="primary" onClick={copyLink}>Kopírovat</Button>
              </div>
              <Button size="sm" variant="ghost" onClick={() => deleteLink.mutate(file.id)} disabled={deleteLink.isPending}>
                Zrušit veřejný odkaz
              </Button>
            </>
          ) : (
            <div>
              <Button size="sm" variant="secondary" onClick={() => createLink.mutate(file.id)} disabled={createLink.isPending}>
                {createLink.isPending ? 'Vytvářím…' : 'Vytvořit veřejný odkaz'}
              </Button>
            </div>
          )}
        </section>
      </div>
    </Modal>
  );
}

/** Hromadné sdílení více dokumentů najednou. */
function BulkShareModal({
  files,
  stavbaId,
  onClose,
}: {
  files: ProjectFile[];
  stavbaId: number;
  onClose: () => void;
}) {
  const toast = useToast();
  const share = useShareProjectDoc(stavbaId);
  const createLink = useCreatePublicLink(stavbaId);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [links, setLinks] = useState<{ name: string; url: string }[]>([]);
  const [msg, setMsg] = useState<string | null>(null);

  async function handleShareAll() {
    const e = email.trim();
    if (!e) return;
    setBusy(true);
    setMsg(null);
    let ok = 0;
    let skip = 0;
    let fail = 0;
    for (const f of files) {
      try {
        await share.mutateAsync({ fileId: f.id, email: e });
        ok++;
      } catch (ex: unknown) {
        const status = (ex as { status?: number })?.status;
        if (status === 409) skip++;
        else fail++;
      }
    }
    setBusy(false);
    if (fail > 0 && ok === 0) {
      setMsg('Sdílení se nezdařilo — zkontrolujte e-mail uživatele.');
    } else {
      setEmail('');
      setMsg(`Nasdíleno ${ok}${skip ? `, ${skip} už existovalo` : ''}${fail ? `, ${fail} selhalo` : ''}.`);
      toast.success(`Nasdíleno ${ok} souborů`);
    }
  }

  async function handleCreateLinks() {
    setBusy(true);
    const out: { name: string; url: string }[] = [];
    for (const f of files) {
      try {
        const link = await createLink.mutateAsync(f.id);
        out.push({ name: f.nazev || f.filename, url: publicUrl(link.token) });
      } catch {
        /* přeskoč */
      }
    }
    setLinks(out);
    setBusy(false);
    toast.success(`Vytvořeno ${out.length} odkazů`);
  }

  async function copyAll() {
    await navigator.clipboard.writeText(links.map((l) => `${l.name}: ${l.url}`).join('\n'));
    toast.success('Odkazy zkopírovány');
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Sdílet ${files.length} dokumentů`}
      size="md"
      footer={<Button variant="secondary" onClick={onClose}>Zavřít</Button>}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--sd-text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Sdílet s uživatelem aplikace
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="e-mail uživatele"
                onKeyDown={(e) => { if (e.key === 'Enter') handleShareAll(); }}
              />
            </div>
            <Button variant="primary" onClick={handleShareAll} disabled={busy || !email.trim()}>
              {busy ? 'Pracuji…' : 'Sdílet všem'}
            </Button>
          </div>
          {msg && <div style={{ fontSize: 12.5, color: 'var(--sd-text-dim)' }}>{msg}</div>}
        </section>

        <section style={{ display: 'flex', flexDirection: 'column', gap: 10, borderTop: '1px solid var(--sd-w-e7e1d4, #e7e1d4)', paddingTop: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--sd-text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Veřejné odkazy ke stažení
          </div>
          <div>
            <Button size="sm" variant="secondary" onClick={handleCreateLinks} disabled={busy}>
              {busy ? 'Vytvářím…' : `Vytvořit veřejné odkazy (${files.length})`}
            </Button>
          </div>
          {links.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {links.map((l) => (
                <div key={l.url} style={{ fontSize: 12, padding: '6px 10px', background: 'var(--sd-w-f7f4ee, #f7f4ee)', border: '1px solid var(--sd-w-e7e1d4, #e7e1d4)', borderRadius: 4 }}>
                  <div style={{ fontWeight: 600 }}>{l.name}</div>
                  <div style={{ fontFamily: 'var(--sd-font-mono, monospace)', color: 'var(--sd-text-dim)', wordBreak: 'break-all' }}>{l.url}</div>
                </div>
              ))}
              <Button size="sm" variant="primary" onClick={copyAll}>Kopírovat všechny odkazy</Button>
            </div>
          )}
        </section>
      </div>
    </Modal>
  );
}
